// Add what a Day Close stock cash-out bought (F-CTRL-N75). The cash-out
// already carries the money; this puts the items into stock and writes no
// money again. Items that come to a different total than the cash taken out
// need a reason (the server's rule: itemsTolerance in handlers/dayops.go).
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { CashDrawerMovement } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatMoney } from '@/lib/currency'
import { cn } from '@/lib/utils'
import { newIdempotencyKey } from '@/lib/inventoryFlow/offlineQueue'
import { BT } from '@/lib/inventoryFlow/boughtStrings'
import type { MarketDeliverTo } from '@/lib/inventoryFlow/marketTypes'
import { itemsDiffer, marketProblem, marketTotal, stockCashOutBody, type MarketDraftLine } from '@/lib/inventoryFlow/marketLogic'
import { BoughtItemsEditor, ChoiceRow, type BillPhoto } from './BoughtItemsEditor'

export function StockCashOutItemsDialog({ movement, onClose, notify }: {
  movement: CashDrawerMovement | null
  onClose: () => void
  notify: (kind: 'success' | 'error', message: string) => void
}) {
  const qc = useQueryClient()
  const [deliverTo, setDeliverTo] = useState<MarketDeliverTo>('store')
  const [stall, setStall] = useState('')
  const [lines, setLines] = useState<MarketDraftLine[]>([])
  const [photos, setPhotos] = useState<BillPhoto[]>([])
  const [note, setNote] = useState('')
  const [key, setKey] = useState(newIdempotencyKey)

  useEffect(() => {
    if (!movement) return
    setDeliverTo('store')
    setStall('')
    setLines([])
    setPhotos([])
    setNote('')
    setKey(newIdempotencyKey())
  }, [movement])

  const cash = movement?.amount ?? 0
  const total = marketTotal(lines)
  const problem = marketProblem(lines, 'till', '')
  const differs = lines.length > 0 && !problem && itemsDiffer(cash, total)
  const save = useMutation({
    mutationFn: () => apiClient.addCashMovementItems(movement!.id, {
      purchase: stockCashOutBody({ deliverTo, supplier: stall, notes: '', lines, photos }),
      ...(differs ? { accept_difference: true, difference_note: note.trim() } : {}),
    }, key),
    onSuccess: () => {
      notify('success', BT.itemsSaved)
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      qc.invalidateQueries({ queryKey: ['stock-cash-outs'] })
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      onClose()
    },
    onError: (e: Error) => notify('error', e.message.split(' — ')[0] || 'Could not add the items'),
  })

  return (
    <Dialog open={!!movement} onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 space-y-1.5 border-b px-6 py-4 pr-12">
          <DialogTitle>{BT.addItemsTitle}</DialogTitle>
          <DialogDescription>{movement ? `${movement.reason} · ${BT.addItemsHelp(formatMoney(cash))}` : ''}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <ChoiceRow label={BT.deliverTo} value={deliverTo} options={(['store', 'kitchen'] as const).map((p) => [p, BT.places[p]] as const)} onChange={setDeliverTo} />
          <BoughtItemsEditor lines={lines} onLines={setLines} photos={photos} onPhotos={setPhotos} onError={(m) => notify('error', m)} />
          <label className="block space-y-1"><span className="text-sm font-medium">{BT.supplier}</span>
            <Input maxLength={150} placeholder={BT.supplierPlaceholder} value={stall} onChange={(e) => setStall(e.target.value)} /></label>
          <dl className="grid grid-cols-2 gap-2 rounded-lg border p-3 text-sm">
            <dt className="text-muted-foreground">{BT.cashTaken}</dt><dd className="text-right font-semibold tabular-nums">{formatMoney(cash)}</dd>
            <dt className="text-muted-foreground">{BT.itemsTotal}</dt>
            <dd className={cn('text-right font-semibold tabular-nums', differs && 'text-danger-fg')}>{formatMoney(total)}</dd>
          </dl>
          {differs && (
            <div className="space-y-1.5">
              <p className="text-sm text-danger-fg">{BT.differs}</p>
              <label className="block space-y-1"><span className="text-sm font-medium">{BT.differenceNote}</span>
                <Input maxLength={500} placeholder={BT.differencePlaceholder} value={note} onChange={(e) => setNote(e.target.value)} /></label>
            </div>
          )}
          {problem && lines.length > 0 && <p className="text-sm text-destructive">{BT.problems[problem]}</p>}
        </div>
        <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>{BT.cancel}</Button>
          <Button disabled={Boolean(problem) || (differs && !note.trim()) || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? BT.saving : BT.saveItems}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
